import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import axios from '../utils/axios';
import { AuthContext } from '../context/AuthContext';
import './styles/UserManager.css';

function chipClass(action) {
  switch (action) {
    case 'create':
    case 'activate':
    case 'profile_pic_update':
      return 'chip ok';
    case 'deactivate':
    case 'delete_soft':
    case 'delete_hard':
      return 'chip danger';
    case 'update':
    case 'access_update':
    case 'role_change':
    default:
      return 'chip';
  }
}

/** One history row item */
function HistoryRow({ entry, masterSitesMap }) {
  const [open, setOpen] = useState(false);
  const when = new Date(entry.at).toLocaleString();

  // Convert masterSite IDs to names in details AND hide Id fields
  const beautify = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    const clone = JSON.parse(JSON.stringify(obj));

    // fields that may contain site IDs we want to map to names
    const MS_FIELDS_TO_MAP = [
      'masterSiteAccess',
      'masterSiteIds',
      'oldAccess',
      'newAccess',
      'added',
      'removed',
      'granted',
      'revoked'
    ];

    // ID-key fields we want to remove from display
    const ID_KEYS_TO_DROP = [
      'masterSiteAccessIds',
      'masterSiteIds'
    ];

    const mapIdToName = (val) => {
      if (Array.isArray(val)) return val.map(v => masterSitesMap.get(String(v)) || String(v));
      if (typeof val === 'string') return masterSitesMap.get(val) || val;
      return val;
    };

    // Drop unwanted ID keys at top level
    ID_KEYS_TO_DROP.forEach(k => { if (k in clone) delete clone[k]; });

    // Map any ID arrays/values to names at top level
    Object.keys(clone).forEach(key => {
      if (MS_FIELDS_TO_MAP.includes(key)) {
        clone[key] = mapIdToName(clone[key]);
      }
    });

    // Handle one level of nesting (common in our audit payloads)
    Object.keys(clone).forEach(key => {
      const v = clone[key];
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        // Drop nested ID keys
        ID_KEYS_TO_DROP.forEach(k => { if (k in v) delete v[k]; });
        // Map nested fields
        Object.keys(v).forEach(sub => {
          if (MS_FIELDS_TO_MAP.includes(sub)) {
            v[sub] = mapIdToName(v[sub]);
          }
        });
      }
    });

    return clone;
  };

  const prettyDetails = entry.details ? beautify(entry.details) : null;

  return (
    <li className="history-item">
      <div className="history-line">
        <span className="when">{when}</span>
        <span className={chipClass(entry.action)}>{entry.action}</span>
        <span className="by">by <strong>{entry.byName || entry.by}</strong></span>
        {prettyDetails && (
          <button className="link" onClick={() => setOpen(o => !o)}>
            {open ? 'Hide details' : 'Show details'}
          </button>
        )}
      </div>

      {open && prettyDetails && (
        <pre className="details-json">
          {JSON.stringify(prettyDetails, null, 2)}
        </pre>
      )}
    </li>
  );
}

/** Inline panel that appears under the clicked user row */
function InlineHistoryPanel({ userId, onClose, masterSitesMap }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setBusy(true);
        const res = await axios.get(`/users/${userId}/history`);
        if (!alive) return;
        const list = Array.isArray(res.data?.history) ? res.data.history : [];
        setItems(list.sort((a, b) => new Date(b.at) - new Date(a.at)));
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e.message);
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => { alive = false; };
  }, [userId]);

  return (
    <div className="history-inline-panel">
      <div className="history-header">
        <div className="title">History</div>
        <button className="btn tiny" onClick={onClose}>Close</button>
      </div>
      {busy && <div className="muted">Loading…</div>}
      {err && <div className="error">{err}</div>}
      {!busy && !err && items.length === 0 && (
        <div className="muted">No activity yet.</div>
      )}
      {!busy && !err && items.length > 0 && (
        <ul className="history-list">
          {items.map((h, i) => (
            <HistoryRow key={`${h.at}-${i}`} entry={h} masterSitesMap={masterSitesMap} />
          ))}
        </ul>
      )}
    </div>
  );
}

export default function UserManager() {
  const { user } = useContext(AuthContext);

  // ====== Role helpers & permissions ======
  const currentRole = String(user?.role || '').toLowerCase();
  const isSuperAdmin = currentRole === 'super-admin';
  const isAdmin = currentRole === 'admin';
  const isManager = currentRole === 'manager' || currentRole === 'manager'.toLowerCase();
  const isTechnician = currentRole === 'technician';

  const canAccessPage = isSuperAdmin || isAdmin || isManager; // (unchanged)
  const canCreate = isSuperAdmin || isAdmin || isManager;
  const canEditDelete = isSuperAdmin || isAdmin;
  const canToggleStatus = isSuperAdmin || isAdmin || isManager;

  // ====== State ======
  const [users, setUsers] = useState([]);
  const [masterSites, setMasterSites] = useState([]);
  const [roles, setRoles] = useState([]); // [{ _id, name }]
  const [message, setMessage] = useState('');

  const [form, setForm] = useState({
    username: '',
    password: '',
    role: 'user',          // role *name*
    status: 'active',
    masterSiteAccess: []
  });

  const [editingId, setEditingId] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [openHistoryFor, setOpenHistoryFor] = useState(null); // which userId expanded

  const fileInputRef = useRef(null);

  const token = localStorage.getItem('token');
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  // Fast lookup of master site name by id
  const masterSitesMap = useMemo(() => {
    const m = new Map();
    (masterSites || []).forEach(ms => m.set(String(ms._id), ms.name));
    return m;
  }, [masterSites]);

  // ====== Effects ======
  useEffect(() => {
    if (!user) return;
    if (canAccessPage) {
      fetchUsers();
      fetchMasterSites();
      fetchRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ====== Data fetchers ======
  const fetchUsers = async () => {
    try {
      const res = await axios.get('/users', authHeader);
      const list = Array.isArray(res.data) ? res.data : [];
      let filtered = list;

      switch (currentRole) {
        case 'super-admin':
          filtered = list; // sees all
          break;
        case 'admin':
          // admin cannot see super-admin or other admins
          filtered = list.filter(u => !['super-admin', 'admin'].includes(String(u.role).toLowerCase()));
          break;
        case 'manager':
          // manager sees only users & technicians
          filtered = list.filter(u => ['user', 'technician'].includes(String(u.role).toLowerCase()));
          break;
        case 'technician':
        case 'user':
          filtered = list.filter(u => u._id === user._id); // self only
          break;
        default:
          filtered = [];
      }

      setUsers(filtered);
    } catch (error) {
      alert('Error fetching users: ' + (error.response?.data?.message || error.message));
    }
  };

  const fetchMasterSites = async () => {
    try {
      const res = await axios.get('/mastersites', authHeader);
      setMasterSites(res.data || []);
    } catch (_) {/* optional toast */ }
  };

  const fetchRoles = async () => {
    try {
      const res = await axios.get('/users/roles', authHeader);
      const fetched = Array.isArray(res.data) ? res.data : [];
      setRoles(fetched);

      // keep form.role valid
      const roleNames = fetched.map(r => r.name);
      if (!roleNames.includes(form.role)) {
        setForm(prev => ({ ...prev, role: roleNames[0] || 'user' }));
      }
    } catch (e) {
      console.error('Error fetching roles:', e);
      setRoles([{ _id: 'fallback', name: 'user' }]);
      setForm(prev => ({ ...prev, role: 'user' }));
    }
  };

  // ====== Helpers ======
  const resetForm = () => {
    setForm({ username: '', password: '', role: 'user', status: 'active', masterSiteAccess: [] });
    setEditingId(null);
    setPhotoFile(null);
    setPhotoPreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadPhoto = async (userId) => {
    if (!photoFile) return;
    const fd = new FormData();
    fd.append('profilePic', photoFile);
    await axios.put(`/users/${userId}/profile-pic`, fd, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
  };

  // ====== Submit (Create/Update) ======
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!canCreate) {
        setMessage('⛔ You do not have permission to create users.');
        return;
      }

      if (editingId) {
        if (!canEditDelete) {
          setMessage('⛔ You do not have permission to edit users.');
          return;
        }

        // update role/status
        await axios.put(
          `/users/${editingId}`,
          { role: form.role, status: form.status },
          authHeader
        );

        // update masterSiteAccess only for non-admin-like roles
        const roleLower = String(form.role || '').toLowerCase();
        const isAdminLikeForm = ['admin', 'super-admin', 'manager', 'technician'].includes(roleLower);
        if (!isAdminLikeForm) {
          await axios.put(
            `/users/${editingId}/access`,
            { masterSiteIds: form.masterSiteAccess },
            authHeader
          );
        }

        await uploadPhoto(editingId);
        setMessage('✅ User updated');
      } else {
        // CREATE
        const payload = {
          username: form.username,
          password: form.password,
          role: form.role,
          status: form.status,
          ...(['admin', 'super-admin', 'manager', 'Manager', 'technician'].includes(form.role)
            ? {}
            : { masterSiteAccess: form.masterSiteAccess })
        };

        await axios.post('/users/register', payload, authHeader);
        setMessage('✅ User created');

        if (photoFile) {
          const res = await axios.get('/users', authHeader);
          const created = (res.data || []).find(u => u.username === form.username);
          if (created?._id) await uploadPhoto(created._id);
        }
      }

      resetForm();
      fetchUsers();
    } catch (err) {
      setMessage('❌ Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // ====== Row actions ======
  const handleEdit = (u) => {
    if (!canEditDelete) return;
    setEditingId(u._id);
    setForm({
      username: u.username,
      password: '',
      role: u.role,
      status: u.status || 'active',
      masterSiteAccess: Array.isArray(u.masterSiteAccess)
        ? u.masterSiteAccess.map(ms => ms._id || ms)
        : []
    });
    setPhotoFile(null);
    setPhotoPreview(u.profilePic || '');
    if (fileInputRef.current) fileInputRef.current.value = '';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!canEditDelete) return;
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`/users/${id}`, authHeader);
      if (openHistoryFor === id) setOpenHistoryFor(null);
      fetchUsers();
    } catch (e) {
      setMessage('❌ Error deleting user: ' + (e.response?.data?.message || e.message));
    }
  };

  const toggleStatus = async (u) => {
    if (!canToggleStatus) return;
    try {

      const next = u.status === 'active' ? 'inactive' : 'active';
      //await axios.put(`/users/${u._id}`, { status: next }, authHeader);
      const isActive = u.status !== 'active'; // flip to the other state
      await axios.patch(`/users/${u._id}/status`, { isActive }, authHeader);
      alert('Status updated successfully');
      fetchUsers();
    } catch (e) {
      setMessage('❌ Error updating status: ' + (e.response?.data?.message || e.message));
    }
  };

  const toggleHistory = (u) => {
    setOpenHistoryFor(prev => (prev === u._id ? null : u._id));
  };

  // ====== Inputs ======
  const handleMultiSelectChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, (o) => o.value);
    setForm(prev => ({ ...prev, masterSiteAccess: selected }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setPhotoFile(file || null);
    setPhotoPreview(file ? URL.createObjectURL(file) : '');
  };

  // ====== Guards ======
  if (!user) return <div>🔄 Loading user info...</div>;
  if (!canAccessPage) return <div>⛔ Access Denied</div>;

  // HIDE Master Sites dropdown for admin-like roles *including technician*
  const roleLowerForForm = String(form.role || '').toLowerCase();
  const roleIsAdminLike = ['admin', 'super-admin', 'manager', 'technician'].includes(roleLowerForForm);

  return (
    <div className="user-manager-container">
      {/* Page header */}
      <div className="um-header">
        <div>
          <h1 className="um-title">User Management</h1>
          <p className="um-subtitle">
            Create users, assign roles, and control access to master sites.
          </p>
        </div>
      </div>

      {message && <div className="message-box">{message}</div>}

      {/* Section title above the form */}
      <h3 className="um-section-title">
        {editingId ? 'Edit User' : 'Create User'}
      </h3>
      {!canCreate && (
        <div className="muted">
          You have read-only access. Only managers and admins can create users.
        </div>
      )}


      <form onSubmit={handleSubmit} className="user-form" encType="multipart/form-data">
        <div className="form-row">
          <div className="col">
            <label>Username</label>
            <input
              type="text"
              placeholder="Username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              required
              disabled={!!editingId}
            />
          </div>

          {!editingId && (
            <div className="col">
              <label>Password</label>
              <input
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
            </div>
          )}

          <div className="col">
            <label>Role</label>
            <select
              className="input-control"
              value={form.role || ''}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              disabled={roles.length === 0}
            >
              {roles.length === 0 && <option value="">Loading roles…</option>}
              {roles.length > 0 && <option value="">Select Role</option>}
              {roles.map((r) => (
                <option key={r._id} value={r.name}>{r.name}</option>
              ))}
            </select>
          </div>

          <div className="col">
            <label>Status</label>
            <select
              className="input-control"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          {!roleIsAdminLike && (
            <div className="col">
              <label>Master Sites</label>
              <select
                multiple
                value={form.masterSiteAccess}
                onChange={handleMultiSelectChange}
                className="multi-select"
              >
                {masterSites.map((ms) => (
                  <option key={ms._id} value={ms._id}>{ms.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="col">
            <label>Profile Photo</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
            {photoPreview && (
              <div className="photo-preview">
                <img src={photoPreview} alt="preview" />
              </div>
            )}
          </div>
        </div>

        <div className="actions">
          <button type="submit" disabled={!canCreate}>
            {editingId ? 'Save Changes' : 'Create User'}
          </button>
          {editingId && (
            <button type="button" className="secondary" onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      </form>

      <h1 className="um-section-title">Existing Users</h1>
      <table className="user-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Role</th>
            <th>Status</th>
            <th>Master Sites</th>
            <th>History</th>
            {(canEditDelete || canToggleStatus) && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const msNames = Array.isArray(u.masterSiteAccess)
              ? u.masterSiteAccess
                .map(ms => (typeof ms === 'object' && ms?.name) ? ms.name : masterSitesMap.get(String(ms)) || String(ms))
                .join(', ')
              : '';

            const rowOpen = openHistoryFor === u._id;

            return (
              <React.Fragment key={u._id}>
                <tr>
                  <td>
                    <div className="user-cell">
                      <img
                        className="avatar"
                        src={
                          u.profilePic
                            ? u.profilePic
                            : 'https://ui-avatars.com/api/?name=' + encodeURIComponent(u.username)
                        }
                        alt={u.username}
                      />
                      <span>{u.username}</span>
                    </div>
                  </td>
                  <td>{u.role}</td>
                  <td>
                    <span className={`status-pill ${u.status === 'active' ? 'ok' : 'off'}`}>
                      {u.status || 'active'}
                    </span>
                  </td>
                  <td>{msNames}</td>
                  <td>
                    <button className="btn tiny" onClick={() => toggleHistory(u)}>
                      {rowOpen ? 'Hide' : 'View'}
                    </button>
                  </td>
                  {(canEditDelete || canToggleStatus) && (
                    <td className="actions-cell">
                      {canToggleStatus && (
                        <button
                          className="small tertiary"
                          onClick={() => toggleStatus(u)}
                          title={u.status === 'active' ? 'Deactivate' : 'Activate'}
                        >
                          {u.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                      {canEditDelete && (
                        <>
                          <button onClick={() => handleEdit(u)}>Edit</button>
                          <button onClick={() => handleDelete(u._id)}>Delete</button>
                        </>
                      )}
                    </td>
                  )}
                </tr>

                {/* Inline expandable history row */}
                {rowOpen && (
                  <tr className="history-inline-row">
                    <td colSpan={(canEditDelete || canToggleStatus) ? 6 : 5}>
                      <InlineHistoryPanel
                        userId={u._id}
                        onClose={() => setOpenHistoryFor(null)}
                        masterSitesMap={masterSitesMap}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
